<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { Sparkles, Lock } from '@lucide/vue';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import { SPREAD_SEO_DATA } from '@app/data/spreadSeoData';
import SeoSpreadPatch from '@app/presentation/components/seo/SeoSpreadPatch.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

useHead({
  title: 'Расклады Таро Онлайн — 16 Видов | Таро Мяу',
  meta: [
    { name: 'description', content: 'Все виды раскладов Таро: карта дня, да или нет, любовный, финансовый, на судьбу, карьеру и другие. Бесплатные и премиум расклады с персональной интерпретацией.' },
    { name: 'keywords', content: 'расклад таро, расклад на любовь, расклад да нет, карта дня таро, расклад на будущее, таро онлайн бесплатно' },
    { property: 'og:title', content: 'Расклады Таро Онлайн — 16 Видов | Таро Мяу' },
    { property: 'og:description', content: 'Все виды раскладов Таро с персональной интерпретацией. Бесплатные и премиум расклады.' },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Расклады Таро Онлайн — 16 Видов | Таро Мяу' },
    { name: 'twitter:description', content: 'Все виды раскладов Таро с персональной интерпретацией. Бесплатные и премиум расклады.' },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: 'https://tarotmeow.ru/rasklady' },
    { rel: 'alternate', hreflang: 'ru', href: 'https://tarotmeow.ru/rasklady' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://tarotmeow.ru/rasklady' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'Расклады', item: 'https://tarotmeow.ru/rasklady' },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Расклады Таро',
        numberOfItems: 16,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Карта дня', url: 'https://tarotmeow.ru/rasklady/karta-dnya' },
          { '@type': 'ListItem', position: 2, name: 'Да или Нет', url: 'https://tarotmeow.ru/rasklady/da-ili-net' },
          { '@type': 'ListItem', position: 3, name: 'Задай вопрос', url: 'https://tarotmeow.ru/rasklady/zadaj-vopros' },
          { '@type': 'ListItem', position: 4, name: 'Расклад на три карты', url: 'https://tarotmeow.ru/rasklady/rasklad-na-tri-karty' },
          { '@type': 'ListItem', position: 5, name: 'А или Б', url: 'https://tarotmeow.ru/rasklady/a-ili-b' },
          { '@type': 'ListItem', position: 6, name: 'Любовный расклад', url: 'https://tarotmeow.ru/rasklady/lyubovnyj-rasklad' },
          { '@type': 'ListItem', position: 7, name: 'Финансовый расклад', url: 'https://tarotmeow.ru/rasklady/finansovyj-rasklad' },
          { '@type': 'ListItem', position: 8, name: 'Расклад Судьбы', url: 'https://tarotmeow.ru/rasklady/rasklad-sudby' },
          { '@type': 'ListItem', position: 9, name: 'Расклад на учебу', url: 'https://tarotmeow.ru/rasklady/rasklad-na-uchebu' },
          { '@type': 'ListItem', position: 10, name: 'Расклад на бывшего', url: 'https://tarotmeow.ru/rasklady/rasklad-na-byvshego' },
          { '@type': 'ListItem', position: 11, name: 'Почему он пропал?', url: 'https://tarotmeow.ru/rasklady/pochemu-on-propal' },
          { '@type': 'ListItem', position: 12, name: 'Ждать или отпустить', url: 'https://tarotmeow.ru/rasklady/zhdat-ili-otpustit' },
          { '@type': 'ListItem', position: 13, name: 'Гадание на ситуацию', url: 'https://tarotmeow.ru/rasklady/gadanie-na-situaciyu' },
          { '@type': 'ListItem', position: 14, name: 'Увольняться нельзя остаться', url: 'https://tarotmeow.ru/rasklady/uvolnyatsya-nelzya-ostatsya' },
          { '@type': 'ListItem', position: 15, name: 'Расклад на талант', url: 'https://tarotmeow.ru/rasklady/rasklad-na-talant' },
          { '@type': 'ListItem', position: 16, name: 'Расклад на самопознание', url: 'https://tarotmeow.ru/rasklady/rasklad-na-samopoznanie' },
        ],
      }),
    },
  ],
});

const SPREADS = [
  { id: 'daily', slug: 'karta-dnya', title: 'Карта дня', description: 'Получи совет на день. Вытяни одну карту и узнай, что ждёт тебя сегодня.', route: '/rasklady/karta-dnya', free: true },
  { id: 'yes-no', slug: 'da-ili-net', title: 'Да или Нет', description: 'Быстрый и чёткий ответ на твой вопрос. Идеально, когда нужна ясность.', route: '/rasklady/da-ili-net', free: true },
  { id: 'question-single', slug: 'zadaj-vopros', title: 'Задай вопрос', description: 'Одна карта — один ответ. Сосредоточься на вопросе и получи ответ.', route: '/rasklady/zadaj-vopros', free: true },
  { id: 'question-three', slug: 'rasklad-na-tri-karty', title: 'Расклад на три карты', description: 'Глубокий анализ вопроса: прошлое, настоящее и будущее.', route: '/rasklady/rasklad-na-tri-karty', free: true },
  { id: 'a-or-b', slug: 'a-ili-b', title: 'А или Б', description: 'Сравни два пути или варианта. Карты покажут плюсы и минусы каждого.', route: '/rasklady/a-ili-b', free: false },
  { id: 'love', slug: 'lyubovnyj-rasklad', title: 'Любовный расклад', description: 'Подробный анализ твоих отношений, чувств партнёра и перспектив.', route: '/rasklady/lyubovnyj-rasklad', free: false },
  { id: 'financial', slug: 'finansovyj-rasklad', title: 'Финансовый расклад', description: 'Советы по деньгам, карьере и финансовым решениям.', route: '/rasklady/finansovyj-rasklad', free: false },
  { id: 'destiny', slug: 'rasklad-sudby', title: 'Расклад Судьбы', description: 'Раскрой свой жизненный путь и предназначение.', route: '/rasklady/rasklad-sudby', free: false },
  { id: 'education', slug: 'rasklad-na-uchebu', title: 'Расклад на учебу', description: 'Советы по обучению, развитию и карьерному росту.', route: '/rasklady/rasklad-na-uchebu', free: false },
  { id: 'ex-partner', slug: 'rasklad-na-byvshego', title: 'Расклад на бывшего', description: 'Анализ прошлых отношений и их влияния на настоящее.', route: '/rasklady/rasklad-na-byvshego', free: false },
  { id: 'disappearance', slug: 'pochemu-on-propal', title: 'Почему он пропал?', description: 'Раскрой причины молчания и узнай, чего ожидать.', route: '/rasklady/pochemu-on-propal', free: false },
  { id: 'wait-or-release', slug: 'zhdat-ili-otpustit', title: 'Ждать или отпустить', description: 'Помощь в сложном решении. Стоит ли ждать или двигаться дальше?', route: '/rasklady/zhdat-ili-otpustit', free: false },
  { id: 'situation', slug: 'gadanie-na-situaciyu', title: 'Гадание на ситуацию', description: 'Глубокий анализ текущих обстоятельств и возможных исходов.', route: '/rasklady/gadanie-na-situaciyu', free: false },
  { id: 'career-crossroads', slug: 'uvolnyatsya-nelzya-ostatsya', title: 'Увольняться нельзя остаться', description: 'Помощь в карьерном выборе: остаться или уйти?', route: '/rasklady/uvolnyatsya-nelzya-ostatsya', free: false },
  { id: 'talent', slug: 'rasklad-na-talant', title: 'Расклад на талант', description: 'Раскрой свои скрытые способности и таланты.', route: '/rasklady/rasklad-na-talant', free: false },
  { id: 'self-discovery', slug: 'rasklad-na-samopoznanie', title: 'Расклад на самопознание', description: 'Познай своё истинное я и найди гармонию.', route: '/rasklady/rasklad-na-samopoznanie', free: false },
];

// Each spread's patch (stock + fabric, as on the app's /spread home) and card count.
const spreads = SPREADS.map((s) => {
  const data = SPREAD_SEO_DATA[s.slug];
  return { ...s, stock: data?.stock ?? 'bg-card', fabric: data?.fabric ?? 'fabric-linen', cardCount: data?.cardCount ?? 1 };
});

const freeSpreads = computed(() => spreads.filter((s) => s.free));
const premiumSpreads = computed(() => spreads.filter((s) => !s.free));
</script>

<template>
  <div class="py-16 md:py-24">
    <div class="site-container">
      <!-- Header -->
      <header class="mb-12 text-center md:mb-16">
        <div class="type-overline mb-3.5 text-primary-ink">
          Колода на любой случай
        </div>
        <h1 class="type-display">Расклады Таро</h1>
        <p class="type-body mx-auto mt-3.5 max-w-2xl text-muted-foreground md:text-[17px]">
          16 видов раскладов с персональной интерпретацией. Бесплатные расклады доступны без регистрации.
        </p>
        <p class="type-caption mx-auto mt-3 max-w-2xl">
          Не знаешь, с чего начать?
          <RouterLink
            to="/kak-zadavat-voprosy-taro"
            class="seo-link font-semibold"
          >Как задавать вопросы Таро</RouterLink>
          — как сформулировать вопрос, чтобы ответ был ясным.
        </p>
      </header>

      <!-- Free Spreads: the same patches as the app's /spread home -->
      <section class="mb-16 md:mb-24">
        <div class="mb-8 flex items-center gap-3">
          <span
            class="seo-tag type-overline shrink-0 bg-success px-2.5 py-0.5 text-success-foreground"
            style="--tilt: -2deg"
          >
            <Sparkles
              class="h-3 w-3"
              aria-hidden="true"
            /> Бесплатно
          </span>
          <h2 class="type-title">Бесплатные расклады</h2>
        </div>

        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <SeoSpreadPatch
            v-for="spread in freeSpreads"
            :key="spread.id"
            :to="spread.route"
            :stock="spread.stock"
            :fabric="spread.fabric"
            :fan="spread.cardCount"
            :fan-width="40"
            class="min-h-[200px] p-6"
          >
            <h3 class="type-heading pr-4">{{ spread.title }}</h3>
            <p class="type-body mb-5 mt-2.5 pr-24 md:pr-28">{{ spread.description }}</p>
            <span
              class="seo-tag paper-tag type-overline mt-auto w-fit px-2.5 py-0.5"
              style="--tilt: -1.5deg"
            >
              <Sparkles
                class="h-3 w-3"
                aria-hidden="true"
              /> Бесплатно
            </span>
          </SeoSpreadPatch>
        </div>
      </section>

      <!-- Premium Spreads -->
      <section>
        <div class="mb-8 flex items-center gap-3">
          <span
            class="seo-tag paper-tag type-overline shrink-0 px-2.5 py-0.5"
            style="--tilt: 1.5deg"
          >
            <Lock
              class="h-3 w-3"
              aria-hidden="true"
            /> Подписка
          </span>
          <h2 class="type-title">Премиум расклады</h2>
        </div>

        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <SeoSpreadPatch
            v-for="(spread, i) in premiumSpreads"
            :key="spread.id"
            :to="spread.route"
            :stock="spread.stock"
            :fabric="spread.fabric"
            :fan="spread.cardCount"
            class="min-h-[200px] p-6"
          >
            <h3 class="type-subheading pr-4">{{ spread.title }}</h3>
            <p class="type-body mb-5 mt-2.5 pr-20">{{ spread.description }}</p>
            <span
              class="seo-tag paper-tag type-overline mt-auto w-fit px-2.5 py-0.5"
              :style="{ '--tilt': `${i % 2 ? 1.2 : -1.5}deg` }"
            >
              <Lock
                class="h-3 w-3"
                aria-hidden="true"
              /> Подписка
            </span>
          </SeoSpreadPatch>
        </div>
      </section>

      <!-- CTA -->
      <ScrollReveal class="mt-16 md:mt-24">
        <SeoFinalCta cat>
          <h2 class="type-title">Попробуй бесплатно</h2>
          <p class="type-body mx-auto mt-3.5 max-w-xl text-muted-foreground md:text-[17px]">
            4 расклада доступны без подписки. Открой приложение и вытяни первую карту прямо сейчас.
          </p>
          <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </div>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>
